import { TutorialDefinition, TutorialScene } from '../TutorialEngine.js';
import { COLORS } from '../../rendering/Renderer.js';
import { BranchesVortexTutorialState } from './BranchesTutorialAdapter.js';

type VState = BranchesVortexTutorialState;

const PARTIAL = 'B2,C4C5,C4D4,B4C4,A4B4,A3A4';
const PARTIAL_GATED = `${PARTIAL}|D4=E2`;
const SOLVED = 'B2,C4C5,C4D4,F1F2,E1F1,D1E1,E2F2,B4C4,A4B4,A3A4|D4=E2';

// Activation scene: gate cells D4/E2 are left empty (the C4D4 segment is dropped),
// so the pair is activatable and toggles on. Branches never block a gate, so there
// is no amber pair here — just the activation animation.
const ACTIVATE = 'B2,C4C5,B4C4,A4B4,A3A4';

const scenes: TutorialScene[] = [
    // Scene 1: Toggle Gates — animate the gate placement flow
    {
        gameState: { branches: PARTIAL } as VState,
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Click Activate Vortex\nto place a gate pair', cell: 'B2', anchor: 'center' },
            { type: 'text-label', text: 'Pair must be linked\nin the Vortex Hub', cell: 'E5', anchor: 'center' },
        ],
        animate: {
            interval: 1500,
            loop: true,
            frames: [
                { branches: PARTIAL },
                { branches: PARTIAL, gateMode: true },
                { branches: PARTIAL_GATED, gateMode: true },
                { branches: PARTIAL_GATED },
            ] as VState[],
        },
    },

    // Scene 2: Activation — toggle the pair active (branches never block a gate)
    {
        gameState: { branches: ACTIVATE, gateMode: true } as VState,
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Click a pair\nto activate it', cell: 'E3', anchor: 'center', color: COLORS.teal },
        ],
        animate: {
            interval: 1500,
            loop: true,
            frames: [
                { branches: ACTIVATE, gateMode: true },
                { branches: `${ACTIVATE}|D4=E2`, gateMode: true },
            ] as VState[],
        },
    },

    // Scene 3: Branch teleportation
    {
        gameState: { branches: SOLVED } as VState,
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Branch enters\na gate...', cell: 'D4', anchor: 'far-left' },
            { type: 'text-label', text: '...exits from\nthe other side', cell: 'C3', anchor: 'far-right' },
            { type: 'pulse', cell: 'D4', color: COLORS.purple },
            { type: 'pulse', cell: 'E2', color: COLORS.purple },
        ],
    },

    // Scene 4: Both sides share distance — full solved state
    {
        gameState: { branches: SOLVED } as VState,
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Both sides share\nthe same distance', cell: 'D3', anchor: 'center', color: COLORS.teal },
            { type: 'pulse', cell: 'D4', color: COLORS.purple },
            { type: 'pulse', cell: 'E2', color: COLORS.purple },
        ],
    },
];

/**
 * Vortex Branches tutorial: 4 scenes on a 6x6 vortex grid with digits 0-3.
 * Teaches gate creation, activation, and branch teleportation through Vortex gates.
 */
export const BRANCHES_VORTEX_TUTORIAL: TutorialDefinition = {
    gameType: 'branches-vortex',
    gridCode: '6/0-3:0C4,1F2,2A3,3D1',
    scenes,
    previewScene: 3,
};
